import { useRef } from "react";
import styled from "styled-components";

const Zone = styled.div`
  border: 2px dashed ${({ theme, $hasFile }) => $hasFile ? theme.colors.primary : theme.colors.lineColor};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 20px 16px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s, background-color 0.2s;
  background-color: ${({ theme, $hasFile }) =>
    $hasFile ? `${theme.colors.primary}15` : "transparent"};
  outline: none;
  margin-bottom: 12px;
  user-select: none;

  &:hover,
  &:focus-visible {
    border-color: ${({ theme }) => theme.colors.primary};
    background-color: ${({ theme }) => `${theme.colors.primary}08`};
  }
`;

const Icon = styled.div`
  font-size: 1.8rem;
  line-height: 1;
  margin-bottom: 6px;
`;

const MainText = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;

const HintText = styled.div`
  font-size: 0.75rem;
  margin-top: 4px;
  opacity: 0.5;
`;

const FileName = styled.div`
  font-size: 0.85rem;
  margin-top: 4px;
  font-weight: 500;
  word-break: break-all;
`;

const ClearBtn = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  font-size: 0.78rem;
  margin-top: 6px;
  padding: 0;
  text-decoration: underline;

  &:hover {
    opacity: 0.7;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

function FileDropZone({ accept, label, icon = "📎", file, onFile, existingLabel }) {
  const inputRef = useRef(null);

  const isPdf = accept === "application/pdf";

  const handleClick = () => inputRef.current?.click();

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) onFile(f);
    // Reset input so same file can be re-selected
    e.target.value = "";
  };

  const handlePaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    for (const item of items) {
      if (item.kind !== "file") continue;
      const f = item.getAsFile();
      if (!f) continue;
      const matches = isPdf
        ? f.type === "application/pdf"
        : f.type.startsWith("image/");
      if (matches) {
        onFile(f);
        e.preventDefault();
        return;
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };

  const fileIcon = isPdf ? "📄" : "🖼️";

  return (
    <Zone
      tabIndex={0}
      $hasFile={!!file}
      onClick={handleClick}
      onPaste={handlePaste}
      onKeyDown={handleKeyDown}
      role="button"
      aria-label={label}
    >
      <HiddenInput
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        onClick={(e) => e.stopPropagation()}
      />

      {file ? (
        <>
          <Icon>{fileIcon}</Icon>
          <FileName>{file.name}</FileName>
          <ClearBtn
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onFile(null);
            }}
          >
            Ukloni
          </ClearBtn>
        </>
      ) : (
        <>
          <Icon>{icon}</Icon>
          <MainText>{label}</MainText>
          {existingLabel && (
            <HintText style={{ opacity: 0.7, fontStyle: "italic" }}>
              {existingLabel}
            </HintText>
          )}
          <HintText>Klikni za odabir datoteke ili zalijepi (Ctrl+V)</HintText>
        </>
      )}
    </Zone>
  );
}

export default FileDropZone;
